import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Search, X, UserPlus } from 'lucide-react';
import debounce from 'lodash/debounce';
import { InterviewWorkspace } from './InterviewWorkspace';

interface Customer {
  id: string;
  full_name: string;
  email: string;
  primary_comms_id?: string;
}

interface CommsPlatform {
  id: string;
  name: string;
}

interface CustomField {
  id: string;
  name: string;
  field_type: string;
  options?: string[];
}

interface CreateInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  customFields: CustomField[];
  onInterviewCreated?: () => void;
}

export function CreateInterviewModal({ 
  isOpen, 
  onClose, 
  projectId, 
  customFields,
  onInterviewCreated
}: CreateInterviewModalProps) {
  const [step, setStep] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [isCreatingInterview, setIsCreatingInterview] = useState(false);
  const [commsPlatforms, setCommsPlatforms] = useState<CommsPlatform[]>([]);
  const [newCustomer, setNewCustomer] = useState({
    fullName: '',
    email: '',
    commsPlatform: ''
  });
  const [formData, setFormData] = useState({
    customerId: '',
    customerName: '',
    customerEmail: '',
    commsPlatform: '',
    customFieldValues: {},
    helpfulScore: 3,
    friendlyScore: 3,
    willingToSpeakAgain: true,
    notes: '',
    interviewId: '',
    interviewType: 'video' as 'video' | 'text',
    videoStoragePath: null as string | null
  });

  // Reset state when modal is opened
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSearchTerm('');
      setSearchResults([]);
      setSelectedCustomer(null);
      setIsCreatingCustomer(false);
      setIsCreatingInterview(false);
      setNewCustomer({
        fullName: '',
        email: '',
        commsPlatform: ''
      });
      setFormData({
        customerId: '',
        customerName: '',
        customerEmail: '',
        commsPlatform: '',
        customFieldValues: {},
        helpfulScore: 3,
        friendlyScore: 3,
        willingToSpeakAgain: true,
        notes: '',
        interviewId: '',
        interviewType: 'video',
        videoStoragePath: null
      });
    }
  }, [isOpen]);

  useEffect(() => {
    fetchCommsPlatforms();
  }, []);

  const searchCustomers = debounce(async (term: string) => {
    if (!term) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('id, full_name, email, primary_comms_id')
        .ilike('full_name', `%${term}%`)
        .limit(5);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (error) {
      console.error('Error searching customers:', error);
    } finally {
      setSearching(false);
    }
  }, 300);

  async function fetchCommsPlatforms() {
    try {
      const { data, error } = await supabase
        .from('communication_platforms')
        .select('*')
        .order('name');

      if (error) throw error;
      setCommsPlatforms(data || []);
    } catch (error) {
      console.error('Error fetching communication platforms:', error);
    }
  }

  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      customerId: customer.id,
      customerName: customer.full_name,
      customerEmail: customer.email,
      commsPlatform: customer.primary_comms_id || ''
    }));
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleCreateCustomer = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data, error } = await supabase
        .from('customers')
        .insert({
          full_name: newCustomer.fullName,
          first_name: newCustomer.fullName.split(' ')[0],
          email: newCustomer.email,
          primary_comms_id: newCustomer.commsPlatform,
          current_owner: user.id
        })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        handleSelectCustomer(data);
        setIsCreatingCustomer(false);
        setNewCustomer({ fullName: '', email: '', commsPlatform: '' });
      }
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  const handleNext = async () => {
    setIsCreatingInterview(true);
    try {
      const { data, error } = await supabase
        .from('interviews')
        .insert({
          project_id: projectId,
          customer_id: formData.customerId,
          comms_platform_id: formData.commsPlatform,
          custom_field_values: formData.customFieldValues,
          notes: formData.notes,
          interview_type: formData.interviewType,
          video_storage_path: null
        })
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setFormData(prev => ({
          ...prev,
          interviewId: data.id
        }));
        setStep(2);
      }
    } catch (error) {
      console.error('Error creating interview:', error);
    } finally {
      setIsCreatingInterview(false);
    }
  };

  const handleSubmit = async (data: any) => {
    try {
      console.log('Finalizing interview with data:', data);
      console.log('Interview ID:', formData.interviewId);

      if (!formData.interviewId) {
        throw new Error('No interview ID found');
      }

      const updateData = {
        notes: data.notes,
        custom_field_values: data.customFieldValues,
        video_storage_path: data.videoStoragePath,
        video_filename: data.videoFilename,
        video_size: data.videoSize,
        video_content_type: data.videoContentType,
        video_uploaded_at: data.videoUploadedAt
      };

      console.log('Update data:', updateData);

      const { error: updateError } = await supabase
        .from('interviews')
        .update(updateData)
        .eq('id', formData.interviewId);

      if (updateError) throw updateError;

      onClose();
      onInterviewCreated?.();
    } catch (error) {
      console.error('Error finalizing interview:', error);
    }
  };

  useEffect(() => {
    searchCustomers(searchTerm);
  }, [searchTerm]);

  if (!isOpen) return null;

  if (step === 2) {
    return (
      <InterviewWorkspace
        isOpen={true}
        onClose={onClose}
        projectId={projectId}
        customFields={customFields}
        formData={formData}
        onSubmit={handleSubmit}
        onSaveComplete={onInterviewCreated}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              onClick={onClose}
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div>
            <h3 className="text-lg font-medium leading-6 text-brand-teal">
              Add New Interview - Step {step} of 2
            </h3>

            <div className="mt-4 relative">
              <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                <div
                  style={{ width: `${(step / 2) * 100}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-brand-orange"
                />
              </div>
            </div>

            <div className="mt-6">
              {!isCreatingCustomer ? (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <label htmlFor="customer-search" className="block text-sm font-medium text-gray-700">
                      Search Customer
                    </label>
                    <button
                      onClick={() => setIsCreatingCustomer(true)}
                      className="inline-flex items-center text-sm text-brand-orange hover:text-brand-orange-light transition-colors"
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      New Customer
                    </button>
                  </div>
                  <div className="mt-1 relative">
                    <div className="relative">
                      <input
                        type="text"
                        id="customer-search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full rounded-lg border-gray-300 pr-10 focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                        placeholder="Type to search..."
                      />
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <Search className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>

                    {searchResults.length > 0 && (
                      <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {searchResults.map((customer) => (
                          <li
                            key={customer.id}
                            className="relative cursor-pointer select-none py-2 pl-3 pr-9 hover:bg-brand-teal hover:text-white transition-colors"
                            onClick={() => handleSelectCustomer(customer)}
                          >
                            <div className="flex items-center">
                              <span className="font-normal block truncate">
                                {customer.full_name}
                              </span>
                            </div>
                            <span className="block truncate text-sm opacity-75">
                              {customer.email}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      value={newCustomer.fullName}
                      onChange={(e) => setNewCustomer({ ...newCustomer, fullName: e.target.value })}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="commsPlatform" className="block text-sm font-medium text-gray-700">
                      Preferred Communication Platform
                    </label>
                    <select
                      id="commsPlatform"
                      value={newCustomer.commsPlatform}
                      onChange={(e) => setNewCustomer({ ...newCustomer, commsPlatform: e.target.value })}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                    >
                      <option value="">Select platform...</option>
                      {commsPlatforms.map(platform => (
                        <option key={platform.id} value={platform.id}>
                          {platform.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setIsCreatingCustomer(false)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateCustomer}
                      disabled={!newCustomer.fullName || !newCustomer.email}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-brand-orange hover:bg-brand-orange-light focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 transition-colors disabled:opacity-50"
                    >
                      Create
                    </button>
                  </div>
                </div>
              )}

              {selectedCustomer && !isCreatingCustomer && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="text-sm font-medium text-brand-teal">Selected Customer</h4>
                  <p className="mt-1 text-sm text-gray-600">{selectedCustomer.full_name}</p>
                  <p className="text-sm text-gray-500">{selectedCustomer.email}</p>
                </div>
              )}

              {selectedCustomer && !isCreatingCustomer && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Interview Type
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="video"
                        checked={formData.interviewType === 'video'}
                        onChange={(e) => setFormData(prev => ({ ...prev, interviewType: e.target.value as 'video' | 'text' }))}
                        className="focus:ring-brand-orange h-4 w-4 text-brand-orange border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Video Call</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="text"
                        checked={formData.interviewType === 'text'}
                        onChange={(e) => setFormData(prev => ({ ...prev, interviewType: e.target.value as 'video' | 'text' }))}
                        className="focus:ring-brand-orange h-4 w-4 text-brand-orange border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Email/DM Correspondence</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-5 sm:mt-6">
            <button
              type="button"
              onClick={handleNext}
              disabled={!selectedCustomer || isCreatingInterview}
              className="w-full inline-flex justify-center rounded-lg border border-transparent bg-brand-orange px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-orange-light focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 transition-colors disabled:opacity-50"
            >
              {isCreatingInterview ? 'Creating...' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}